import { useState } from "react";
import { useTranslation } from "@localization/useTranslation.ts";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { getLink } from "../../../taskManagement/taskPane/taskRelationsBox/TaskRelationsBox.hooks.ts";
import { DeletionBlockerGroup, DeletionBlockerItem } from "./DeleteBlockedDialog.types.ts";

export function useDeleteBlockedDialog(
    blockers: Array<DeletionBlockerGroup>,
    blockerTranslationKeys: Record<number, string>,
    blockerNoAccessTranslationKeys?: Partial<Record<number, string>>
) {
    const { translateString } = useTranslation();
    const urlProvider = useUrlProvider();

    // The first area is open so the dialog never reads as empty, the rest are collapsed to keep it scannable.
    const [expandedType, setExpandedType] = useState<number | undefined>(blockers[0]?.type);

    const toggleExpanded = (type: number) => setExpandedType(expandedType === type ? undefined : type);

    const getBlockerName = (group: DeletionBlockerGroup) =>
        `${translateString(blockerTranslationKeys[group.type])} (${group.totalCount + group.noAccessCount})`;

    const getItemUrl = (group: DeletionBlockerGroup, item: DeletionBlockerItem) =>
        group.entityType === undefined ? undefined : getLink(group.entityType, item.id, item.parentId, urlProvider);

    const isTruncated = (group: DeletionBlockerGroup) => group.totalCount > group.items.length;

    const getNoAccessText = (group: DeletionBlockerGroup) =>
        translateString(blockerNoAccessTranslationKeys?.[group.type] ?? "deletionBlockerNoAccessCount", { count: group.noAccessCount });

    return {
        expandedType,
        toggleExpanded,
        getBlockerName,
        getItemUrl,
        isTruncated,
        getNoAccessText,
    };
}
